import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  Grid,  List,
  ListItem,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Divider,
  Box,
  Snackbar,
  Alert,
  Avatar
} from '@mui/material';
import CustomListItemText from '../components/CustomListItemText';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import ApiService from '../services/apiService';

const CompetitorForm = () => {
  // Parameter aus der URL abrufen (für Bearbeitungsmodus)
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  // Zustand für den Wettbewerber und seine Pakete
  const [competitor, setCompetitor] = useState({
    name: '',
    logo_path: null
  });
  
  // Zustand für das ausgewählte Logo
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');

  // Zustand für die Pakete des Wettbewerbers
  const [packages, setPackages] = useState([]);  // Zustand für das Paket-Dialog
  const [packageDialog, setPackageDialog] = useState({
    open: false,
    isEdit: false,
    packageId: null,
    name: '',
    percentage_fee: 0,
    fixed_fee: 0,
    is_flatrate: false,
    flatrate_amount: 0,
    min_ticket_price: '',
    max_ticket_price: '',
    is_tax_inclusive: true // Standardmäßig inkl. Mehrwertsteuer
  });
    // Variablen für die direkte Verwendung des packageDialog-Zustands definieren
  const [logoFile, setLogoFile] = useState(null);
    // Formular zurücksetzen
  const resetPackageForm = () => {
    setPackageDialog({
      open: false,
      isEdit: false,
      packageId: null,
      name: '',
      percentage_fee: 0,
      fixed_fee: 0,
      is_flatrate: false,
      flatrate_amount: 0,
      min_ticket_price: '',
      max_ticket_price: '',
      is_tax_inclusive: true // Standardmäßig inkl. Mehrwertsteuer
    });
  };
  
  // Zustand für den Löschdialog
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    packageId: null,
    packageName: ''
  });
  
  // Zustand für die Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Wettbewerber laden, wenn ID vorhanden ist (Bearbeitungsmodus)
  useEffect(() => {
    if (id) {
      // Bestehenden Wettbewerber laden
      ApiService.getCompetitorById(id)
        .then(data => {
          if (data) {
            setCompetitor({
              name: data.name,
              logo_path: data.logo_path
            });
            setPackages(data.packages || []);
          } else {
            // Wettbewerber nicht gefunden
            setSnackbar({
              open: true,
              message: 'Wettbewerber nicht gefunden',
              severity: 'error'
            });
            navigate('/competitors');
          }
        })
        .catch(error => {
          console.error('Fehler beim Laden des Wettbewerbers:', error);
          setSnackbar({
            open: true,
            message: 'Fehler beim Laden des Wettbewerbers',
            severity: 'error'
          });
        });
    }
  }, [id, navigate]);

  // Handler für Änderungen am Wettbewerbernamen
  const handleNameChange = (event) => {
    setCompetitor(prev => ({
      ...prev,
      name: event.target.value
    }));
  };
  // Handler für Logo-Upload
  const handleLogoChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSelectedLogo(file);
      setLogoFile(file); // Setze die logoFile-Variable
      
      // Logo-Vorschau erstellen
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  // Paketdialog öffnen (zur Bearbeitung oder Erstellung)
  const openPackageDialog = (pkg = null) => {
    if (pkg) {
      // Bearbeitungsmodus
      setPackageDialog({
        open: true,
        isEdit: true,
        packageId: pkg.id,
        name: pkg.name,
        percentage_fee: pkg.percentage_fee || 0,
        fixed_fee: pkg.fixed_fee || 0,
        is_flatrate: !!pkg.is_flatrate,
        flatrate_amount: pkg.flatrate_amount || 0,
        min_ticket_price: pkg.min_ticket_price || '',
        max_ticket_price: pkg.max_ticket_price || '',
        is_tax_inclusive: pkg.is_tax_inclusive !== undefined ? pkg.is_tax_inclusive : true // Wenn definiert, verwende Wert, sonst default true
      });
    } else {
      // Erstellungsmodus
      setPackageDialog({
        open: true,
        isEdit: false,
        packageId: null,
        name: '',
        percentage_fee: 0,
        fixed_fee: 0,
        is_flatrate: false,
        flatrate_amount: 0,
        min_ticket_price: '',
        max_ticket_price: '',
        is_tax_inclusive: true // Standardmäßig inkl. Mehrwertsteuer
      });
    }
  };

  // Paketdialog schließen
  const closePackageDialog = () => {
    setPackageDialog(prev => ({
      ...prev,
      open: false
    }));
  };

  // Handler für Änderungen an den Paketdaten
  const handlePackageChange = (field) => (event) => {
    let value = event.target.value;
    
    // Bei Zahlenfeldern in Zahl umwandeln
    if (['percentage_fee', 'fixed_fee', 'flatrate_amount', 'min_ticket_price', 'max_ticket_price'].includes(field)) {
      value = event.target.value === '' ? '' : parseFloat(event.target.value);
    }
      // Bei Checkbox-Feldern den checked-Status verwenden
    if (field === 'is_flatrate' || field === 'is_tax_inclusive') {
      value = event.target.checked;
    }
    
    setPackageDialog(prev => ({
      ...prev,
      [field]: value
    }));
  };  // Paket speichern (erstellen oder aktualisieren)
  const handleSavePackage = () => {
    if (!packageDialog.isEdit) {
      // Neues Paket mit Wettbewerber-ID
      const newPackage = {
        name: packageDialog.name,
        percentage_fee: packageDialog.percentage_fee,
        fixed_fee: packageDialog.fixed_fee,
        is_flatrate: packageDialog.is_flatrate ? 1 : 0,
        flatrate_amount: packageDialog.flatrate_amount,
        min_ticket_price: packageDialog.min_ticket_price || null,
        max_ticket_price: packageDialog.max_ticket_price || null,
        is_tax_inclusive: packageDialog.is_tax_inclusive,
        competitor_id: parseInt(id)
      };
      
      // Paket zum Server senden
      ApiService.createPackage(newPackage)
        .then(response => {
          // Pakete neu laden oder zur Liste hinzufügen
          setPackages(prev => [...prev, response]);
          
          // Dialog schließen und Formular zurücksetzen
          resetPackageForm();
          
          // Erfolgsmeldung anzeigen
          setSnackbar({
            open: true,
            message: 'Paket erfolgreich erstellt',
            severity: 'success'
          });
        })
        .catch(error => {
          console.error('Fehler beim Erstellen des Pakets:', error);
          setSnackbar({
            open: true,
            message: 'Fehler beim Erstellen des Pakets',
            severity: 'error'
          });
        });
    } else {      // Bestehendes Paket aktualisieren
      const updatedPackage = {
        name: packageDialog.name,
        percentage_fee: packageDialog.percentage_fee,
        fixed_fee: packageDialog.fixed_fee,
        is_flatrate: packageDialog.is_flatrate ? 1 : 0,
        flatrate_amount: packageDialog.flatrate_amount,
        min_ticket_price: packageDialog.min_ticket_price || null,
        max_ticket_price: packageDialog.max_ticket_price || null,
        is_tax_inclusive: packageDialog.is_tax_inclusive,
        competitor_id: parseInt(id)
      };
      
      ApiService.updatePackage(packageDialog.packageId, updatedPackage)
        .then(response => {
          // Paket in der Liste aktualisieren
          setPackages(prev => 
            prev.map(pkg => pkg.id === packageDialog.packageId ? response : pkg)
          );
          
          // Dialog schließen und Formular zurücksetzen
          resetPackageForm();
          
          // Erfolgsmeldung anzeigen
          setSnackbar({
            open: true,
            message: 'Paket erfolgreich aktualisiert',
            severity: 'success'
          });
        })
        .catch(error => {
          console.error('Fehler beim Aktualisieren des Pakets:', error);
          setSnackbar({
            open: true,
            message: 'Fehler beim Aktualisieren des Pakets',
            severity: 'error'
          });
        });
    }
  };
  
  // Dialog zum Löschen eines Pakets öffnen
  const openDeleteDialog = (packageId, packageName) => {
    setDeleteDialog({
      open: true,
      packageId,
      packageName
    });
  };

  // Dialog zum Löschen eines Pakets schließen
  const closeDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      packageId: null,
      packageName: ''
    });
  };

  // Paket löschen
  const handleDeletePackage = (packageId) => {
    ApiService.deletePackage(packageId)
      .then(success => {
        if (success) {
          // Paket aus der Liste entfernen
          setPackages(prev => prev.filter(pkg => pkg.id !== packageId));
          
          // Erfolgsmeldung anzeigen
          setSnackbar({
            open: true,
            message: 'Paket erfolgreich gelöscht',
            severity: 'success'
          });
        } else {
          throw new Error('Fehler beim Löschen des Pakets');
        }
      })
      .catch(error => {
        console.error('Fehler beim Löschen des Pakets:', error);
        setSnackbar({
          open: true,
          message: 'Fehler beim Löschen des Pakets',
          severity: 'error'
        });
      });
  };

  // Formular absenden (Wettbewerber speichern oder aktualisieren)
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    try {
      let response;
      
      // Logo-Verarbeitung, wenn vorhanden
      if (logoFile) {
        const base64Logo = await ApiService.saveImageAsBase64(logoFile);
        competitor.logo_path = base64Logo;
      }
      
      if (id) {
        // Wettbewerber aktualisieren
        response = await ApiService.updateCompetitor(id, competitor);
      } else {
        // Neuen Wettbewerber erstellen
        response = await ApiService.createCompetitor(competitor);
      }
      
      // Erfolgsmeldung anzeigen
      setSnackbar({
        open: true,
        message: id ? 'Wettbewerber erfolgreich aktualisiert' : 'Wettbewerber erfolgreich erstellt',
        severity: 'success'
      });
        // Nach dem Speichern zur Bearbeitungsseite des Wettbewerbers navigieren,
      // damit direkt Pakete hinzugefügt werden können
      if (!id && response && response.id) {
        // Kurze Verzögerung, um sicherzustellen, dass die Daten verarbeitet wurden
        setTimeout(() => {
          navigate(`/competitors/edit/${response.id}`);
        }, 100);
      }
    } catch (error) {
      console.error('Fehler beim Speichern des Wettbewerbers:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Speichern des Wettbewerbers',
        severity: 'error'
      });
    }
  };

  // Handler für das Schließen der Snackbar
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <>
      <Typography variant="h4" gutterBottom>
        {isEditMode ? 'Wettbewerber bearbeiten' : 'Neuer Wettbewerber'}
      </Typography>

      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Wettbewerbername"
                fullWidth
                required
                value={competitor.name}
                onChange={handleNameChange}
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{ textAlign: 'center' }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="logo-upload"
                  type="file"
                  onChange={handleLogoChange}
                />
                <label htmlFor="logo-upload">
                  <Box sx={{ mb: 1 }}>
                    {logoPreview || competitor.logo_path ? (
                      <Avatar
                        src={logoPreview || competitor?.logo_path}
                        alt="Logo Vorschau"
                        sx={{ width: 80, height: 80, margin: '0 auto' }}
                      />
                    ) : (
                      <Avatar sx={{ width: 80, height: 80, margin: '0 auto' }}>
                        <UploadIcon />
                      </Avatar>
                    )}
                  </Box>
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                    size="small"
                  >
                    Logo auswählen
                  </Button>
                </label>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/competitors')}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
            >
              Speichern
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Paketliste (nur im Bearbeitungsmodus) */}
      {isEditMode && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5">Pakete</Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => openPackageDialog()}
            >
              Neues Paket
            </Button>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <List>
            {packages.length > 0 ? (
              packages.map((pkg) => (                <ListItem key={pkg.id} divider>
                  <CustomListItemText
                    primary={pkg.name}
                    secondary={
                      <>                        {pkg.is_flatrate ? (
                          <span>Flatrate: {pkg.flatrate_amount} €</span>
                        ) : (
                          <span>{pkg.percentage_fee}% + {pkg.fixed_fee} €</span>
                        )}
                        {pkg.is_tax_inclusive !== undefined && (
                          <span> {pkg.is_tax_inclusive ? "(inkl. MwSt.)" : "(zzgl. 19% MwSt.)"}</span>
                        )}
                        {(pkg.min_ticket_price || pkg.max_ticket_price) && " ("}
                        {pkg.min_ticket_price && `min: ${pkg.min_ticket_price} €`}
                        {pkg.min_ticket_price && pkg.max_ticket_price && ", "}
                        {pkg.max_ticket_price && `max: ${pkg.max_ticket_price} €`}
                        {(pkg.min_ticket_price || pkg.max_ticket_price) && ")"}
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => openPackageDialog(pkg)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton edge="end" onClick={() => openDeleteDialog(pkg.id, pkg.name)}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))
            ) : (              <ListItem>
                <CustomListItemText primary="Keine Pakete vorhanden" />
              </ListItem>
            )}
          </List>
        </Paper>
      )}

      {/* Dialog zum Hinzufügen oder Bearbeiten eines Pakets */}
      <Dialog
        open={packageDialog.open}
        onClose={closePackageDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {packageDialog.isEdit ? 'Paket bearbeiten' : 'Neues Paket'}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Paketname"
            fullWidth
            required
            value={packageDialog.name}
            onChange={handlePackageChange('name')}
            margin="normal"
          />

          <FormControlLabel
            control={
              <Switch
                checked={packageDialog.is_flatrate}
                onChange={handlePackageChange('is_flatrate')}
                color="primary"
              />
            }
            label="Flatrate"
            sx={{ my: 2 }}
          />

          {/* Steuerstatus Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={packageDialog.is_tax_inclusive}
                onChange={handlePackageChange('is_tax_inclusive')}
                color="primary"
              />
            }
            label="Inklusive 19% MwSt."
            sx={{ my: 2, ml: 3 }}
          />

          {packageDialog.is_flatrate ? (
            <TextField
              label="Flatrate-Betrag (€)"
              type="number"
              fullWidth
              required
              value={packageDialog.flatrate_amount}
              onChange={handlePackageChange('flatrate_amount')}
              InputProps={{
                inputProps: { step: 0.01, min: 0 }
              }}
              margin="normal"
            />
          ) : (
            <>
              <TextField
                label="Prozentuale Gebühr (%)"
                type="number"
                fullWidth
                required
                value={packageDialog.percentage_fee}
                onChange={handlePackageChange('percentage_fee')}                InputProps={{
                  inputProps: { step: 0.01, min: 0 }
                }}
                margin="normal"
              />

              <TextField
                label="Fixe Gebühr (€)"
                type="number"
                fullWidth
                required
                value={packageDialog.fixed_fee}
                onChange={handlePackageChange('fixed_fee')}
                InputProps={{
                  inputProps: { step: 0.01, min: 0 }
                }}
                margin="normal"
              />
            </>
          )}

          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle1" gutterBottom>
            Preisbeschränkungen (optional)
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Mindestpreis (€)"
                type="number"
                fullWidth
                value={packageDialog.min_ticket_price}
                onChange={handlePackageChange('min_ticket_price')}
                InputProps={{
                  inputProps: { step: 0.01, min: 0 }
                }}
                margin="normal"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Höchstpreis (€)"
                type="number"
                fullWidth
                value={packageDialog.max_ticket_price}
                onChange={handlePackageChange('max_ticket_price')}
                InputProps={{
                  inputProps: { step: 0.01, min: 0 }
                }}
                margin="normal"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePackageDialog}>Abbrechen</Button>
          <Button onClick={handleSavePackage} variant="contained" color="primary">
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog zum Bestätigen des Löschens eines Pakets */}
      <Dialog
        open={deleteDialog.open}
        onClose={closeDeleteDialog}
      >
        <DialogTitle>Paket löschen</DialogTitle>
        <DialogContent>
          <Typography>
            Sind Sie sicher, dass Sie das Paket "{deleteDialog.packageName}" löschen möchten?
            Diese Aktion kann nicht rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Abbrechen</Button>
          <Button onClick={handleDeletePackage} color="error">Löschen</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar für Benachrichtigungen */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CompetitorForm;